/**
 * Years and leap days (1.K-1 deliverable 3), over absolute years: the calendar's own count, in
 * which year `yearZero` begins at tick 0. Closed-form for every year whose ticks fit: no loop over
 * years, so a date 280 million years from the epoch resolves as fast as one in year 1.
 */
import { add, floorDiv, floorMod, mul, sub } from './math.js';
import { isCompiled } from './types.js';
/** True when `year` is a leap year: at or after the rule's start, a whole number of intervals on. */
export function yearIsLeap(model, year) {
    const leap = model.leap;
    if (leap === null || year < leap.start)
        return false;
    return floorMod(year, leap.interval) === floorMod(leap.start, leap.interval);
}
/**
 * The number of leap years in [from, year), none before the rule's start; NaN when unsafe. Counted
 * from `from` (or the start, if later) rather than from the start, so a rule starting far from both
 * years cannot overflow a count whose answer is small.
 */
function leapsBetween(leap, from, year) {
    const start = Math.max(from, leap.start);
    if (year <= start)
        return 0;
    // year − start may not be a safe integer, so count by residues: ⌈(y − r) / interval⌉ at each end.
    const r = floorMod(leap.start, leap.interval);
    const upTo = (y) => add(floorDiv(y, leap.interval), floorMod(y, leap.interval) > r ? 1 : 0);
    return sub(upTo(year), upTo(start));
}
/**
 * Days from the start of `yearZero` to the start of `year` (negative before `yearZero`), or NaN
 * when the result is not a safe integer. Common and leap years are counted apart, so the two
 * products share a sign and no intermediate overflow hides a safe result.
 */
export function daysBefore(model, year) {
    const n = sub(year, model.yearZero);
    const leap = model.leap;
    if (leap === null)
        return mul(n, model.commonYearDays);
    const leaps = year >= model.yearZero
        ? leapsBetween(leap, model.yearZero, year)
        : sub(0, leapsBetween(leap, year, model.yearZero));
    return add(mul(sub(n, leaps), model.commonYearDays), mul(leaps, model.leapYearDays));
}
export function yearLength(model, year) {
    return yearIsLeap(model, year) ? model.leapYearDays : model.commonYearDays;
}
/** A month's length in a common or a leap year. */
export function monthLength(month, leap) {
    return leap ? month.leapDays : month.commonDays;
}
export function monthOffset(month, leap) {
    return leap ? month.leapOffset : month.commonOffset;
}
/**
 * The absolute year holding day `days` (days since the epoch) and the day's index in it, or null
 * when that year is not a safe integer. A binary search over the closed-form `daysBefore`, between
 * bounds set by the shortest and the longest year.
 */
export function yearOfDays(model, days) {
    const shortest = Math.min(model.commonYearDays, model.leapYearDays);
    const longest = Math.max(model.commonYearDays, model.leapYearDays);
    const a = floorDiv(days, longest);
    const b = floorDiv(days, shortest);
    let lo = clampSafe(model.yearZero + Math.min(a, b) - 2);
    let hi = clampSafe(model.yearZero + Math.max(a, b) + 2);
    // Invariant: the answer is the largest year in [lo, hi] whose start is at or before `days`.
    while (lo < hi) {
        const mid = lo + Math.floor((hi - lo + 1) / 2);
        const start = daysBefore(model, mid);
        const atOrBefore = Number.isNaN(start) ? mid < model.yearZero : start <= days;
        if (atOrBefore)
            lo = mid;
        else
            hi = mid - 1;
    }
    const dayOfYear = sub(days, daysBefore(model, lo));
    if (!(dayOfYear >= 0 && dayOfYear < yearLength(model, lo)))
        return null;
    return { year: lo, dayOfYear };
}
function clampSafe(n) {
    return Math.min(Number.MAX_SAFE_INTEGER, Math.max(-Number.MAX_SAFE_INTEGER, n));
}
/** The month a day index of a year falls in (months of length 0 that year are skipped). */
export function monthAt(model, leap, dayOfYear) {
    const months = model.months;
    let found = months[0];
    for (const month of months) {
        if (monthLength(month, leap) > 0 && monthOffset(month, leap) <= dayOfYear)
            found = month;
    }
    return { month: found, day: dayOfYear - monthOffset(found, leap) + 1 };
}
/** The start tick of `year`, or NaN when it is not a safe integer. */
export function yearStartTick(model, year) {
    return mul(daysBefore(model, year), model.secondsPerDay);
}
function safeYear(calendar, year) {
    return isCompiled(calendar) && typeof year === 'number' && Number.isSafeInteger(year);
}
/** Whether `year` is a leap year; null without a compiled calendar or a safe-integer year. */
export function isLeapYear(calendar, year) {
    if (!safeYear(calendar, year))
        return null;
    return yearIsLeap(calendar, year);
}
/** The days of `year`; null without a compiled calendar or a safe-integer year. */
export function daysInYear(calendar, year) {
    if (!safeYear(calendar, year))
        return null;
    return yearLength(calendar, year);
}
/** The months present in `year`, in ordinal order, with their lengths that year. */
export function monthsOfYear(calendar, year) {
    if (!safeYear(calendar, year))
        return null;
    const leap = yearIsLeap(calendar, year);
    const out = [];
    for (const month of calendar.months) {
        const days = monthLength(month, leap);
        if (days > 0) {
            out.push({
                key: month.key,
                intercalary: month.intercalary,
                days,
                firstDayIndex: monthOffset(month, leap),
            });
        }
    }
    return out;
}
/** Days from the start of `yearZero` to the start of `year`; null when not a safe integer. */
export function daysBeforeYear(calendar, year) {
    if (!safeYear(calendar, year))
        return null;
    const days = daysBefore(calendar, year);
    return Number.isNaN(days) ? null : days;
}
/** Takes a tick apart, or null when its year is not a safe integer. */
export function locate(model, tick) {
    const days = floorDiv(tick, model.secondsPerDay);
    const found = yearOfDays(model, days);
    if (found === null)
        return null;
    const leap = yearIsLeap(model, found.year);
    const { month, day } = monthAt(model, leap, found.dayOfYear);
    return {
        days,
        second: floorMod(tick, model.secondsPerDay),
        year: found.year,
        dayOfYear: found.dayOfYear,
        leap,
        month,
        day,
    };
}
//# sourceMappingURL=year.js.map